import { useMemo, useRef } from "react";
import { api } from "../api";
import { CountryInput } from "../components/CountryInput";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { NeighbourMap } from "../components/neighbours/NeighbourMap";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface Named {
  code: string;
  name: string;
}
interface View {
  country: Named;
  total: number;
  found: Named[];
  wrong: Named[];
  hints: string[];
  answer?: Named[];
}

/** The wave of light round the country gets its moment before the sheet. */
const OPTS = { sheetDelayMs: 1900 };

const COPY = {
  unknown_country: "Не знаю такої країни",
  already_guessed: "Вже було",
  thats_the_country: "Це і є країна дня 🙃",
};

export function Susidy({ date }: { date?: string | undefined }) {
  const g = useGame<View>("susidy", COPY, date, OPTS);
  const restoredWrong = useRef<number | null>(null);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = useMemo(
    () => new Set([...(state?.view.found ?? []), ...(state?.view.wrong ?? [])].map((x) => x.name)),
    [state?.view.found, state?.view.wrong],
  );

  const submit = async (name: string) => {
    const next = await g.run(() => api.guess<View>("susidy", name, date));
    if (next && next.status === "playing") {
      const extra = next.extra as { hit?: boolean; name?: string } | undefined;
      if (extra?.hit === false) {
        haptic.error();
        g.showToast(`${extra.name ?? "Це"} не межує`);
      }
    }
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  if (restoredWrong.current === null) restoredWrong.current = v.wrong.length;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="СУСІДИ" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <CountryInput disabled={finished} busy={g.busy} exclude={guessed} onSubmit={(name) => void submit(name)} />
      <div
        className="mx-auto w-full px-4 py-2"
        // Height-bound on short screens so the door pile stays in view.
        style={{ maxWidth: "min(460px, calc((var(--tg-viewport-stable-height, 100vh) - 250px) * 1.333))" }}
      >
        <div className="mb-1.5 text-center text-sm text-tghint">
          знайдено <b className="text-tgtext">{v.found.length}</b> з {v.total} сусідів
        </div>
        <NeighbourMap host={v.country} found={v.found.map((f) => f.code)} hints={v.hints} status={s.status} />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-2">
        {v.wrong.length > 0 && (
          <div className="mt-1">
            <div className="text-[11px] uppercase tracking-wide text-tghint">не межують ({v.wrong.length})</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {v.wrong.map((w, i) => (
                <span
                  key={w.code}
                  className={`flex items-center gap-1 rounded-md bg-cold/15 px-2 py-0.5 text-xs ${i >= (restoredWrong.current ?? 0) ? "nm-away" : ""}`}
                >
                  <img src={`/flags/${w.code.toLowerCase()}.svg`} alt="" className="h-3.5 w-5 rounded-[2px] object-cover" /> {w.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {!finished && (
        <div className="flex gap-2 px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pt-1">
          <div className="flex flex-1 items-center text-xs text-tghint">промахів: {v.wrong.length}</div>
          <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("susidy", date))} />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Всі на місці!" : "Здався"}
          subtitle={
            won
              ? `${v.wrong.length === 0 ? "без промахів" : `${v.wrong.length} промахів`} · стрік ${s.me.streak} 🔥`
              : `знайшов ${v.found.length}/${v.total} · стрік згорів`
          }
          scoreLabel="avg пром."
          formatScore={(n) => `${n ?? "–"}`}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
